/**
 * JitterBuffer — small TCP-aware smoothing buffer for the JT/T1078 path.
 *
 * The relay delivers VIDEO_FRAME messages over a TCP-bridged WebSocket, so
 * frames are guaranteed in-order — there is nothing to reorder. The reason
 * this buffer exists is **rate smoothing**: TCP bursts (Nagle + congestion
 * window opening) can deliver 5 frames in a 2 ms window followed by 200 ms
 * of silence. Feeding the decoder this raw cadence produces user-visible
 * stutter even when no frames are lost.
 *
 * Strategy: buffer until the oldest frame has aged `targetMs` (the cushion,
 * e.g. 1240 ms), then a PTS playhead advances at `rate` × wall-clock and
 * releases frames it has passed. `rate` slows (≥SLOWPLAY_MIN_RATE) when the
 * buffer underfills so it refills by gentle slow-motion instead of a hard
 * pause, and speeds up (≤CATCHUP_MAX_RATE) when overfull so the cushion shrinks
 * back to target. No frame dropping, no IDR forcing; the decoder re-anchors on
 * the next natural keyframe after any loss.
 *
 * `targetMs` adapts to observed inter-arrival jitter:
 *   - measure p95 of the inter-push gap over a rolling window
 *   - target = clamp(p95 × 1.5, [minMs, maxMs])
 *
 * Default knobs: 500 ms target, [500, 1240] ms range. Queue bounded to
 * MAX_QUEUED_FRAMES.
 *
 * NOT a reorder buffer. NOT a packet-level retransmit. NOT for WebRTC.
 */

export interface JitterBufferOptions {
  /** Initial target buffer depth in ms. Default 500. */
  initialTargetMs?: number;
  /** Lower clamp. Default 500. */
  minTargetMs?: number;
  /** Upper clamp. Default 1240. */
  maxTargetMs?: number;
  /** Rolling window size (frames) for p95 jitter measurement. Default 60. */
  jitterWindowFrames?: number;
}

interface BufferedFrame<T> {
  frame: T;
  /** Wall-clock timestamp when the frame was pushed (performance.now()). */
  recvAt: number;
  /** Media PTS in microseconds (from the VIDEO_FRAME message). */
  ptsUs: number;
  /** Keyframe flag — emitted alongside the frame so the gate can short-circuit. */
  isKeyframe: boolean;
}

/**
 * Max frames held before dropping the oldest (memory bound). Must exceed the
 * buffer depth in frames at the highest fps — maxTargetMs (1240) @30fps ≈ 37
 * frames (@60fps ≈ 74) — plus headroom so a post-stall burst is paced out by
 * the playhead (catch-up ≤1.10×) instead of dropping the oldest frames and
 * skipping the OSD forward. 25 (the old value, ≈1s @25fps) was BELOW the target
 * depth, so the cushion could never fill and every burst > 1s dropped its head.
 * 256 leaves ample headroom for the 1240 ms cushion. Matches the mobile SDK's
 * MAX_QUEUED_FRAMES.
 */
const MAX_QUEUED_FRAMES = 256;
/**
 * Adaptive playback-rate bounds for the paced playhead. Below the target depth
 * the playhead slows toward SLOWPLAY_MIN_RATE (≤8% slow — barely perceptible) so
 * a drained buffer refills by gentle slow-motion instead of a hard pause. Above
 * target it speeds up toward CATCHUP_MAX_RATE (≤10% fast) so the cushion shrinks
 * back to target (letting `adapt()` lowering targetMs actually lower latency).
 * rate = clamp(1 + RATE_GAIN·(fill−1), [MIN, MAX]); fill = occupancy/target.
 */
const SLOWPLAY_MIN_RATE = 0.92;
const CATCHUP_MAX_RATE = 1.1;
const RATE_GAIN = 0.08;
/** A front-frame PTS this far (ms) from the playhead is a discontinuity
 *  (camera restart / PTS rebase), not jitter — snap the playhead to it. */
const DISCONTINUITY_MS = 3000;
/**
 * Hard ceiling on how far the playhead may fall behind the newest buffered
 * frame. The gentle CATCHUP_MAX_RATE (≤10% fast) burns only 0.1 s of latency
 * per second, so after a stall+burst the MAX_QUEUED_FRAMES cushion (256 ≈ 10 s
 * @25fps) parks the playhead ~10 s behind live and never recovers. Above this
 * ceiling the playhead switches to AGGRESSIVE_CATCHUP_RATE — a steady
 * fast-forward that trims the excess smoothly (no frame drop, no jump) until
 * latency is back under the ceiling. Buffered latency is bounded to ~MAX_LATENCY_MS.
 */
const MAX_LATENCY_MS = 3000;
/** Playhead rate while over MAX_LATENCY_MS. 2.0× recovers a 10 s backlog in
 *  ~7 s. Video only (no audio) so a brief fast-forward is acceptable. Tunable. */
const AGGRESSIVE_CATCHUP_RATE = 2.0;

export class JitterBuffer<T> {
  private readonly minTargetMs: number;
  private readonly maxTargetMs: number;
  private readonly jitterWindowFrames: number;

  private targetMs: number;
  private queue: BufferedFrame<T>[] = [];
  private lastRecvAt: number | null = null;
  private interArrivals: number[] = [];

  // Paced-release playhead (media PTS, ms) + wall-clock anchor.
  private playing = false;
  private anchorWallMs = 0;
  private anchorPtsMs = 0;

  // PLAN-live-field-r2-round2-player.md §3 E/F: how often overflow trimming
  // ran, and how often it had no keyframe to trim to and fell back to
  // dropping the single oldest frame — read via getStats() so the bench can
  // report whether either ever fires on a given trace.
  private _overflowTrims = 0;
  private _overflowOldestDropFallback = 0;
  get overflowTrims(): number { return this._overflowTrims; }
  get overflowOldestDropFallback(): number { return this._overflowOldestDropFallback; }

  constructor(opts: JitterBufferOptions = {}) {
    this.minTargetMs = opts.minTargetMs ?? 500;
    this.maxTargetMs = opts.maxTargetMs ?? 1240;
    this.jitterWindowFrames = opts.jitterWindowFrames ?? 60;
    this.targetMs = Math.max(
      this.minTargetMs,
      Math.min(this.maxTargetMs, opts.initialTargetMs ?? 500),
    );
  }

  /**
   * Add a frame to the buffer. Records its receive timestamp and updates the
   * inter-arrival jitter window.
   */
  push(frame: T, ptsUs: number, isKeyframe: boolean): void {
    const now = performance.now();
    if (this.lastRecvAt !== null) {
      const dt = now - this.lastRecvAt;
      this.interArrivals.push(dt);
      if (this.interArrivals.length > this.jitterWindowFrames) {
        this.interArrivals.shift();
      }
    }
    this.lastRecvAt = now;

    // Push every frame in arrival order. The relay delivers frames in decode
    // order over TCP, so arrival order IS the correct order — we must NOT
    // reorder. A previous "keyframes bypass smoothing" optimization unshifted
    // keyframes to the FRONT of the queue, jumping them ahead of the previous
    // GOP's trailing delta frames still buffered. That reordered PTS — keyframe
    // N.0 emitted before the queued N-0.1 delta — which the WebCodecs decoder
    // rejects ("Decoding error") and the FrameQualityGate misreads as a
    // backward-PTS anchor loss, producing perpetual "recovering" churn.
    // Keyframes still drain promptly: they carry the smallest backlog wait and
    // the gate re-anchors on them as soon as they reach the decoder in order.
    this.queue.push({ frame, recvAt: now, ptsUs, isKeyframe });

    // Memory bound: never hold more than MAX_QUEUED_FRAMES. §3 E: trim to
    // the next keyframe boundary rather than one frame at a time — oldest
    // drop can leave the queue front on a mid-GOP delta, which (once fed)
    // references decoder state from a frame we just discarded. Falls back
    // to oldest-drop only if no keyframe is queued within the overflow.
    if (this.queue.length > MAX_QUEUED_FRAMES) {
      const mustDrop = this.queue.length - MAX_QUEUED_FRAMES;
      let keyIdx = -1;
      for (let i = mustDrop; i < this.queue.length; i += 1) {
        if (this.queue[i].isKeyframe) { keyIdx = i; break; }
      }
      this.queue.splice(0, keyIdx >= 0 ? keyIdx : mustDrop);
      this._overflowTrims += 1;
      if (keyIdx < 0) this._overflowOldestDropFallback += 1;
    }
  }

  /**
   * Release frames due for presentation. Phase 1 buffers until the oldest frame
   * has aged `targetMs` (builds the cushion), then a PTS playhead advances at
   * `rate` × wall-clock and releases frames it has passed. `rate` slows toward
   * SLOWPLAY_MIN_RATE when the buffer is underfull (refill by slow-motion
   * instead of a hard pause) and speeds toward CATCHUP_MAX_RATE when overfull
   * (shrink the cushion back to target). No frame dropping, no IDR forcing.
   * (A separate monotonic-PTS guard in the adapter drops backward frames.)
   */
  drain(): BufferedFrame<T>[] {
    const now = performance.now();
    if (this.queue.length === 0) return [];

    const frontPtsMs = this.queue[0].ptsUs / 1000;

    if (!this.playing) {
      if (now - this.queue[0].recvAt < this.targetMs) return [];
      this.playing = true;
      this.anchorWallMs = now;
      this.anchorPtsMs = frontPtsMs;
    }

    // Discontinuity (camera restart / PTS rebase): snap the playhead to the
    // front PTS instead of crawling across a multi-second gap.
    if (Math.abs(frontPtsMs - this.anchorPtsMs) > DISCONTINUITY_MS) {
      this.anchorPtsMs = frontPtsMs;
      this.anchorWallMs = now;
    }

    // Occupancy = media buffered ahead of the playhead (MAX queued PTS, so a
    // backward-PTS dip doesn't under-count it and trip false slow-play).
    let newestPtsMs = this.anchorPtsMs;
    for (const f of this.queue) {
      const p = f.ptsUs / 1000;
      if (p > newestPtsMs) newestPtsMs = p;
    }
    const occupancyMs = newestPtsMs - this.anchorPtsMs;
    const fill = this.targetMs > 0 ? occupancyMs / this.targetMs : 1;
    // Over the latency ceiling: steady fast-forward that trims the excess
    // smoothly until back under MAX_LATENCY_MS. Otherwise the gentle paced rate
    // (refill by slow-motion / shrink cushion by ≤10% fast).
    const rate =
      occupancyMs > MAX_LATENCY_MS
        ? AGGRESSIVE_CATCHUP_RATE
        : Math.max(
            SLOWPLAY_MIN_RATE,
            Math.min(CATCHUP_MAX_RATE, 1 + RATE_GAIN * (fill - 1)),
          );

    const playheadPtsMs = this.anchorPtsMs + (now - this.anchorWallMs) * rate;
    this.anchorPtsMs = playheadPtsMs;
    this.anchorWallMs = now;

    const out: BufferedFrame<T>[] = [];
    while (this.queue.length > 0 && this.queue[0].ptsUs / 1000 <= playheadPtsMs) {
      out.push(this.queue[0]);
      this.queue.shift();
    }
    return out;
  }

  /**
   * Update the adaptive target based on the current jitter window. Should be
   * called periodically (e.g. every 1 s) — calling it on every push would
   * dominate CPU during burst arrivals.
   */
  adapt(): void {
    if (this.interArrivals.length < 10) return;
    const sorted = this.interArrivals.slice().sort((a, b) => a - b);
    const p95 = sorted[Math.floor(sorted.length * 0.95)];
    const next = p95 * 1.5;
    this.targetMs = Math.max(this.minTargetMs, Math.min(this.maxTargetMs, next));
  }

  /** Wipe queue + jitter window. Call on detach or on VIDEO_INIT (resolution change). */
  reset(): void {
    this.queue = [];
    this.lastRecvAt = null;
    this.interArrivals = [];
    this.playing = false;
    this.anchorWallMs = 0;
    this.anchorPtsMs = 0;
  }

  /** Current adaptive target in ms — for telemetry/debug. */
  get currentTargetMs(): number {
    return this.targetMs;
  }

  /** Number of frames waiting to drain — for telemetry/debug. */
  get pendingFrames(): number {
    return this.queue.length;
  }
}
